import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EMBLEM_URL, NAV_LINKS } from "@/data/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-[#282D37]/80 bg-[#0A0B0D]/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" data-testid="nav-brand" className="group flex items-center gap-3">
            <img
              src={EMBLEM_URL}
              alt="Arsenal Forge emblem"
              className="w-9 transition-transform duration-500 group-hover:scale-110"
            />
            <span className="font-display text-lg font-bold uppercase tracking-[0.25em] text-[#F4F5F7]">
              Arsenal <span className="text-[#E52E2E]">Forge</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testid}
                className={({ isActive }) =>
                  `group relative font-mono text-xs font-medium tracking-[0.25em] transition-colors duration-300 ${
                    isActive ? "text-[#E52E2E]" : "text-[#9EA4B0] hover:text-[#F4F5F7]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-px bg-[#E52E2E] transition-[width] duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-territoria"
              className="border border-[#E52E2E]/60 px-5 py-2.5 font-mono text-xs font-bold tracking-[0.25em] text-[#E52E2E] transition-colors duration-300 hover:bg-[#E52E2E] hover:text-white"
            >
              WORK WITH US
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-[#F4F5F7] lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0A0B0D]/97 px-8 backdrop-blur-2xl lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {[{ label: "HOME", to: "/", testid: "nav-mobile-home" }, ...NAV_LINKS].map((link, i) => (
                <motion.div
                  key={link.to + link.label}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-${link.testid}`}
                    className={({ isActive }) =>
                      `block border-b border-[#282D37]/60 py-5 font-display text-4xl font-bold uppercase tracking-wide transition-colors duration-300 ${
                        isActive ? "text-[#E52E2E]" : "text-[#F4F5F7]"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-12 font-mono text-xs tracking-[0.3em] text-[#5F6573]"
            >
              WE FORGE WORLDS.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
